import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';

/**
 * Public Host Profile interface
 */
export interface PublicHostProfile {
  hostId: string;
  name: string;
  profilePicture?: string;
  bio?: string;
  joinedDate: string;
  verificationLevel?: string;
  responseRate?: number;
  responseTime?: string;
  totalReviews?: number;
  averageRating?: number;
  languages?: string[];
  isSuperhost?: boolean;
  location?: {
    city?: string;
    state?: string;
    country?: string;
  };
}

// Query keys for host profiles
export const hostProfileKeys = {
  all: ['hostProfile'] as const,
  profiles: () => [...hostProfileKeys.all, 'profile'] as const,
  profile: (hostId: string) => [...hostProfileKeys.profiles(), hostId] as const,
};

/**
 * Hook to fetch public host profile by host ID
 */
export const useHostProfile = (hostId: string, enabled: boolean = true) => {
  const isValidId = Boolean(hostId && hostId.trim() !== '' && hostId !== 'undefined' && hostId !== 'null');
  
  return useQuery({
    queryKey: hostProfileKeys.profile(hostId),
    queryFn: async (): Promise<PublicHostProfile> => {
      if (!isValidId) {
        throw new Error('Invalid host ID provided');
      }
      const response = await apiClient.get<PublicHostProfile>(`/public/hosts/${hostId}`);
      return response.data;
    },
    enabled: enabled && isValidId,
    staleTime: 10 * 60 * 1000, // 10 minutes - host profiles don't change often
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Hook to prefetch host profile (for hover effects, etc.)
 */
export const usePrefetchHostProfile = () => {
  const queryClient = useQueryClient();

  return (hostId: string) => {
    queryClient.prefetchQuery({
      queryKey: hostProfileKeys.profile(hostId),
      queryFn: async (): Promise<PublicHostProfile> => {
        const response = await apiClient.get<PublicHostProfile>(`/public/hosts/${hostId}`);
        return response.data;
      },
      staleTime: 10 * 60 * 1000,
    });
  };
};